import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Calendar, Edit, Trash2, Instagram, Youtube, Linkedin, Twitter, Facebook } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface SocialMediaPost {
  id: string;
  title: string;
  content: string;
  platform: string;
  scheduled_date: string;
  status: string;
  image_url: string | null;
  hashtags: string[] | null;
  notes: string | null;
  created_at: string;
}

const platforms = [
  { value: "instagram", label: "Instagram", icon: Instagram, color: "bg-pink-500" },
  { value: "youtube", label: "YouTube", icon: Youtube, color: "bg-red-500" },
  { value: "linkedin", label: "LinkedIn", icon: Linkedin, color: "bg-blue-600" },
  { value: "twitter", label: "Twitter/X", icon: Twitter, color: "bg-sky-500" },
  { value: "facebook", label: "Facebook", icon: Facebook, color: "bg-blue-700" },
  { value: "tiktok", label: "TikTok", icon: Calendar, color: "bg-black" },
];

const statuses = [
  { value: "draft", label: "Draft", color: "bg-gray-500" },
  { value: "scheduled", label: "Scheduled", color: "bg-blue-500" },
  { value: "published", label: "Published", color: "bg-green-500" },
];

const DashboardContentPlanner = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<SocialMediaPost | null>(null);
  
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    platform: "",
    scheduled_date: "",
    status: "draft",
    image_url: "",
    hashtags: "",
    notes: "",
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    loadPosts();
  };

  const loadPosts = async () => {
    const { data, error } = await supabase
      .from("social_media_posts")
      .select("*")
      .order("scheduled_date", { ascending: true });

    if (error) {
      toast.error("Error loading posts");
      console.error(error);
    } else {
      setPosts(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const postData = {
      user_id: user.id,
      title: formData.title,
      content: formData.content,
      platform: formData.platform,
      scheduled_date: formData.scheduled_date,
      status: formData.status,
      image_url: formData.image_url || null,
      hashtags: formData.hashtags ? formData.hashtags.split(",").map(tag => tag.trim()) : null,
      notes: formData.notes || null,
    };

    if (editingPost) {
      const { error } = await supabase
        .from("social_media_posts")
        .update(postData)
        .eq("id", editingPost.id);

      if (error) {
        toast.error("Error updating post");
      } else {
        toast.success("Post updated successfully");
        loadPosts();
        resetForm();
      }
    } else {
      const { error } = await supabase
        .from("social_media_posts")
        .insert([postData]);

      if (error) {
        toast.error("Error creating post");
      } else {
        toast.success("Post created successfully");
        loadPosts();
        resetForm();
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this post?")) return;

    const { error } = await supabase
      .from("social_media_posts")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Error deleting post");
    } else {
      toast.success("Post deleted successfully");
      loadPosts();
    }
  };

  const handleEdit = (post: SocialMediaPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      platform: post.platform,
      scheduled_date: post.scheduled_date,
      status: post.status,
      image_url: post.image_url || "",
      hashtags: post.hashtags ? post.hashtags.join(", ") : "",
      notes: post.notes || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      platform: "",
      scheduled_date: "",
      status: "draft",
      image_url: "",
      hashtags: "",
      notes: "",
    });
    setEditingPost(null);
    setIsDialogOpen(false);
  };

  const getPlatformIcon = (platform: string) => {
    const found = platforms.find(p => p.value === platform);
    if (!found) return Calendar;
    return found.icon;
  };

  const getPlatformColor = (platform: string) => {
    const found = platforms.find(p => p.value === platform);
    return found?.color || "bg-gray-500";
  };

  const getStatusColor = (status: string) => {
    const found = statuses.find(s => s.value === status);
    return found?.color || "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="space-y-8">
          <div className="flex items-center justify-between animate-fade-in">
            <div>
              <h1 className="text-4xl font-bold">Content Planner</h1>
              <p className="text-xl text-muted-foreground mt-2">
                Plan and schedule your social media content
              </p>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button size="lg">
                  <Plus className="w-5 h-5 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPost ? "Edit Post" : "Create New Post"}</DialogTitle>
                  <DialogDescription>
                    Plan your social media content and schedule it for publication
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="Post title or topic"
                    />
                  </div>

                  <div>
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required
                      placeholder="Your post content..."
                      rows={6}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="platform">Platform *</Label>
                      <Select 
                        value={formData.platform}
                        onValueChange={(value) => setFormData({ ...formData, platform: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          {platforms.map((platform) => (
                            <SelectItem key={platform.value} value={platform.value}>
                              {platform.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="status">Status *</Label>
                      <Select 
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="scheduled_date">Scheduled Date & Time *</Label>
                    <Input
                      id="scheduled_date"
                      type="datetime-local"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="image_url">Image URL</Label>
                    <Input
                      id="image_url"
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div>
                    <Label htmlFor="hashtags">Hashtags</Label>
                    <Input
                      id="hashtags"
                      value={formData.hashtags}
                      onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                      placeholder="hashtag1, hashtag2, hashtag3"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Separate with commas</p>
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes or reminders..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingPost ? "Update Post" : "Create Post"}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {posts.length === 0 ? (
            <Card className="border-2 animate-fade-in">
              <CardContent className="py-16 text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No Posts Planned Yet</h3>
                <p className="text-muted-foreground mb-6">
                  Start planning your content calendar by creating your first post
                </p>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {posts.map((post, index) => {
                const PlatformIcon = getPlatformIcon(post.platform);
                return (
                  <Card
                    key={post.id}
                    className="border-2 hover:border-primary transition-all hover:shadow-xl hover:shadow-primary/20 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`w-10 h-10 rounded-lg ${getPlatformColor(post.platform)} flex items-center justify-center text-white`}>
                              <PlatformIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <CardTitle className="text-xl">{post.title}</CardTitle>
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(post.scheduled_date), "PPP 'at' p")}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Badge className={`${getStatusColor(post.status)} text-white`}>
                              {post.status}
                            </Badge>
                            <Badge variant="outline">
                              {platforms.find(p => p.value === post.platform)?.label}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(post)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDelete(post.id)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="whitespace-pre-wrap mb-3">
                        {post.content}
                      </CardDescription>
                      
                      {post.hashtags && post.hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.hashtags.map((tag, i) => (
                            <span key={i} className="text-sm text-primary">#{tag}</span>
                          ))}
                        </div>
                      )}
                      
                      {post.notes && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <p className="text-sm font-medium mb-1">Notes:</p>
                          <p className="text-sm text-muted-foreground">{post.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardContentPlanner;
